import type { RegionDef } from '@playnets/sim';

/**
 * REGIONS
 *
 * Each planet lives somewhere, and where it lives decides what attacks it. A
 * region owns a palette and an enemy roster, so travelling is a change of
 * colour *and* a change of problem — the Forge Belt wants heavy single hits
 * for its armour, the Scrapfields want you to pick a priority target, the Deep
 * Well wants radar.
 *
 * Rosters overlap deliberately at the common end (drones, asteroids) so the
 * basics carry across and only the local speciality is new to learn.
 */
export const REGIONS: readonly RegionDef[] = [
  {
    id: 'inner-reach',
    name: 'The Inner Reach',
    subtitle: 'Warm, close in, and forgiving. Nothing here is trying very hard.',
    homePlanet: 'terra',
    roster: ['drone', 'asteroid', 'burrower', 'cruiser', 'feint'],
    boss: 'world-eater',
  },
  {
    id: 'forge-belt',
    name: 'The Forge Belt',
    subtitle: 'Slag and fast fire. Armour plating that shrugs off chip damage.',
    homePlanet: 'ignis',
    roster: ['cinder', 'slagback', 'asteroid', 'drone', 'carrier'],
    boss: 'world-eater',
  },
  {
    id: 'outer-rim',
    name: 'The Outer Rim',
    subtitle: 'Slow, enormous, patient. Everything out here has time.',
    homePlanet: 'glacia',
    roster: ['rime-drifter', 'asteroid', 'burrower', 'cruiser', 'drone'],
    boss: 'world-eater',
  },
  {
    id: 'scrapfields',
    name: 'The Scrapfields',
    subtitle: 'It repairs itself faster than you can chip at it. Pick a target.',
    homePlanet: 'machina',
    roster: ['scrapper', 'drone', 'feint', 'burrower', 'carrier'],
    boss: 'world-eater',
  },
  {
    id: 'deep-well',
    name: 'The Deep Well',
    subtitle: 'Half of what is coming is not on the radar yet.',
    homePlanet: 'umbra',
    roster: ['void-moth', 'wraith', 'feint', 'cruiser', 'asteroid'],
    boss: 'world-eater',
  },
  {
    id: 'beyond',
    name: 'Beyond',
    subtitle: 'Unsurveyed. Everything here is armoured, and nothing explains itself.',
    homePlanet: 'kepler-9b',
    roster: ['husk', 'slagback', 'void-moth', 'burrower', 'carrier'],
    boss: 'world-eater',
  },
];

export function regionForPlanet(planetId: string): RegionDef {
  return REGIONS.find((r) => r.homePlanet === planetId) ?? REGIONS[0]!;
}
